<script lang="ts" setup>
import type { Section } from '~/types'

interface Props extends Pick<Section, 'collection' | 'id' | 'title' | 'description' | 'subtitle' | 'subdescription' | 'subdescription2'> {}

const props = defineProps<Props>()
const pb = useNuxtApp().$pb
const col = computed<CollectionConfig>(() => GetCollection(props.collection))
const { data } = useAsyncData(`highlights-${props.id}-${props.collection}`, async () => {
  const c = GetCollection(props.collection)
  const collection = c?.pub?.collection ?? c?.collection
  if (!collection)
    return ['add']
  const data = await pb.collection(collection).getList(1, 5, { sort: '@random' }).catch(() => EMPTY_LIST_RESULT)
  return [...data.items, 'more', 'add']
})

const classes = getCollectionClass(props.collection)

const cardComponent = computed(() => {
  if (col.value?.pub?.cardComponent)
    return col.value?.pub?.cardComponent
  return 'div'
})
</script>

<template>
  <section class="py-24 lg:py-32 xl:py-40 relative z-20">
    <div class=" px-6 md:px-12 lg:px-20 xl:px-32  mx-auto max-w-screen-2xl w-full flex flex-wrap gap-3 justify-between items-start">
      <div class="flex flex-col relative z-50">
        <HeadingText
          v-motion-roll-visible-once-right
          tag="h2"
          class="mb-6"
          :class="classes.heading"
          :delay="200"
        >
          {{ props.title }}
        </HeadingText>
        <div
          v-motion-slide-visible-bottom
          class="page-h"
          :class="classes.paragraph"
          :delay="400"
        >
          <section
            data-allow-mismatch
            v-html="props.description"
          />
        </div>
      </div>
      <!-- <PageButtons v-motion-slide-visible-once-bottom :buttons="expand?.buttons" class="pt-3" :delay="300" /> -->
    </div>
    <div class="pt-12 md:pt-20 lg:pt-24">
      <ClientOnly>
        <template v-if="data?.length && props.collection">
          <UCarousel
            :items="data"
            arrows
            :ui="{ item: 'basis-full md:basis-1/2 3xl:basis-1/3 px-3 2xl:px-4 snap-end ', container: '2xl:pl-32 overflow-y-visible pl-6 md:pl-12 lg:pl-32', indicators: { wrapper: 'relative mt-12 z-20' } }"
            indicators
          >
            <template #default="{ item, index }">
              <div
                :id="`tabpanel-${collection}-${index + 1}`"
                class="w-full py-6"
                :delay="100 * (index + 1)"
              >
                <CollectionMoreCard
                  v-if="item === 'more'"
                  :collection="col"
                />
                <CollectionAdvertiseHere
                  v-else-if="item === 'add'"
                  :collection="col"
                  class="h-full w-full"
                  :title="subtitle"
                  :description="subdescription"
                  :button-text="subdescription2"
                />
                <component
                  :is="cardComponent"
                  v-else
                  :data="item"
                  class="h-full"
                />
              </div>
            </template>

            <template #indicator="{ onClick, page, active }">
              <UButton

                role="tab"
                :tabindex="active ? '0' : '-1'"
                :aria-selected="active"
                :aria-controls="`tabpanel-${collection}-${page}`"
                :color="active ? 'yellow' : 'pink'"
                size="2xs"
                class="rounded-full min-w-6 justify-center px-2"
                :aria-label="`Carousel page ${page}`"
                @click="onClick(page)"
              />
            </template>

            <template #prev="{ onClick, disabled }">
              <transition name="fade">
                <UButton
                  v-show="!disabled"
                  class="absolute top-1/2 -mt-5 left-2 z-20 shadow-lg"
                  color="primary"
                  variant="soft"
                  aria-label="Previous"
                  @click="onClick"
                >
                  <Icon
                    name="gbf:arrow-right"
                    class="w-3 lg:w-5 h-5 rotate-180"
                  />
                </UButton>
              </transition>
            </template>

            <template #next="{ onClick, disabled }">
              <transition name="fade">
                <UButton
                  v-show="!disabled"
                  class="absolute top-1/2 -mt-5 right-2 z-50 shadow-lg"
                  color="primary"
                  aria-label="Next"
                  variant="soft"
                  @click="onClick"
                >
                  <Icon
                    name="gbf:arrow-right"
                    class="w-3 lg:w-5 h-5"
                  />
                </UButton>
              </transition>
            </template>
          </UCarousel>
        </template>
      </ClientOnly>
    </div>
  </section>
</template>
